import { useState, useCallback, useEffect, useRef } from 'react'
import {
    uploadFileToUrl,
    autoCropImage,
    compressImage,
    validateImageFile,
} from '@wtf/shared'
import { convertHeic, isHeicFile } from '@wtf/shared'
import { finalizeUpload } from '@wtf/shared/api/uploads'
import { UPLOAD_TYPE_CONFIGS } from '@/config/uploadTypeConfigs'
import {
    UseImageUploadOptions,
    UseImageUploadReturn,
    UploadState,
    FilePickerProps,
    CropperProps,
} from '@wtf/shared'
import { captureException } from '@/config/sentry'

/**
 * Ultimate image upload hook with complete workflow management
 *
 * Handles: file selection → cropping → compression → upload → auto-save
 * Returns ready-to-use props for bottom sheets and complete state management
 *
 * @example
 * ```tsx
 * const {
 *   previewUrl,
 *   isUploading,
 *   triggerUpload,
 *   filePickerProps,
 *   cropperProps
 * } = useImageUpload({
 *   type: ImageUploadType.USER_COVER,
 *   initialPreview: profile.coverUrl,
 *   autoSave: {
 *     mutation: updateUser,
 *     entityId: profile.id,
 *     field: 'coverUrl'
 *   }
 * })
 *
 * // Then just:
 * <button onClick={triggerUpload}>Upload Cover</button>
 * <img src={previewUrl} />
 * <ChooseFileBottomSheet {...filePickerProps} />
 * <ImageCropperBottomSheet {...cropperProps} />
 * ```
 */
export function useImageUpload(
    options: UseImageUploadOptions
): UseImageUploadReturn {
    // Auto-configuration based on upload type
    const config = UPLOAD_TYPE_CONFIGS[options.type]
    const presignMutation = config.presignHook()

    // Merge custom cropping config with defaults
    const cropConfig = { ...config.cropConfig, ...options.customCropping }

    // State management
    const [state, setState] = useState<UploadState>(UploadState.IDLE)
    const [previewUrl, setPreviewUrl] = useState(options.initialPreview || '')
    const [selectedImageUrl, setSelectedImageUrl] = useState('')
    const [uploadError, setUploadError] = useState<string | null>(null)

    // Refs for cleanup
    const objectUrlsRef = useRef<Set<string>>(new Set())

    // Cleanup function for object URLs
    const cleanupObjectUrl = useCallback((url: string) => {
        if (url && url.startsWith('blob:')) {
            URL.revokeObjectURL(url)
            objectUrlsRef.current.delete(url)
        }
    }, [])

    // Cleanup all object URLs on unmount
    useEffect(() => {
        return () => {
            objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
            objectUrlsRef.current.clear()
        }
    }, [])

    // Upload handler - shared logic for both cropped and direct uploads
    const uploadFile = useCallback(
        async (file: File) => {
            setState(UploadState.UPLOADING)

            try {
                // Compress the image
                const compressedFile = await compressImage(
                    file,
                    config.compressionPreset
                )

                // Get presigned URL
                const { uploadUrl, publicUrl, key } =
                    await presignMutation.mutateAsync({
                        fileName: compressedFile.name,
                        contentType: compressedFile.type,
                    })

                // Upload to Cloudflare R2
                await uploadFileToUrl(uploadUrl, compressedFile)

                // Finalize upload (convert HEIC to JPEG if needed)
                let finalUrl = publicUrl
                try {
                    const finalizeResult = await finalizeUpload({
                        key: key,
                        contentType: compressedFile.type,
                    })
                    finalUrl = finalizeResult.finalUrl

                    if (finalizeResult.converted) {
                        console.log(
                            '[useImageUpload] HEIC converted to JPEG:',
                            finalizeResult.finalUrl
                        )
                    }
                } catch (finalizeError) {
                    console.error(
                        '[useImageUpload] Finalize failed, using original URL:',
                        finalizeError
                    )
                    // Continue with original URL if finalization fails
                }

                // Update preview with final URL
                setPreviewUrl(finalUrl)

                // Auto-save if configured
                if (options.autoSave) {
                    setState(UploadState.AUTO_SAVING)

                    try {
                        await options.autoSave.mutation.mutateAsync({
                            id: options.autoSave.entityId,
                            data: { [options.autoSave.field]: finalUrl },
                        })
                        options.autoSave.onSuccess?.(finalUrl)
                    } catch (saveError) {
                        console.error('Auto-save failed:', saveError)
                        options.autoSave.onError?.(saveError as Error)
                        // Continue anyway - the image is uploaded
                    }
                }

                setState(UploadState.SUCCESS)
                options.onSuccess?.(finalUrl)

                // Return to idle after a brief success state
                setTimeout(() => setState(UploadState.IDLE), 500)
            } catch (error) {
                captureException(error, { uploadType: options.type })
                const errorMessage =
                    error instanceof Error
                        ? error.message
                        : 'Failed to upload image'
                setUploadError(errorMessage)
                setState(UploadState.ERROR)
                options.onError?.(errorMessage)

                // Revert preview on error
                setPreviewUrl(options.initialPreview || '')
            }
        },
        [config, presignMutation, options]
    )

    // Common file processing logic
    const processFile = useCallback(
        async (file: File) => {
            setState(UploadState.IDLE)
            setUploadError(null)

            // Validate file
            const validationError = validateImageFile(file)
            if (validationError) {
                setUploadError(validationError)
                setState(UploadState.ERROR)
                options.onError?.(validationError)
                return false
            }

            // Convert HEIC to JPEG/PNG if needed (BEFORE cropping - Canvas can't handle HEIC)
            let fileToProcess = file
            if (isHeicFile(file)) {
                try {
                    // Set PROCESSING state to show loading indicator during conversion
                    setState(UploadState.PROCESSING)

                    fileToProcess = await convertHeic(file)
                    console.log(
                        `[useImageUpload] Converted HEIC to JPEG/PNG before cropping: ${fileToProcess.name}`
                    )
                } catch (error) {
                    const errorMessage =
                        error instanceof Error
                            ? error.message
                            : 'Failed to convert HEIC file'
                    setUploadError(errorMessage)
                    setState(UploadState.ERROR)
                    options.onError?.(errorMessage)
                    return false
                }
            }

            // If cropping is not required, upload directly
            if (!cropConfig.required) {
                await uploadFile(fileToProcess)
                return true
            }

            // If auto-crop is enabled, crop automatically without UI
            if (cropConfig.autoCrop) {
                try {
                    const croppedBlob = await autoCropImage(
                        fileToProcess,
                        cropConfig.aspectRatio
                    )
                    const croppedFile = new File(
                        [croppedBlob],
                        config.defaultFileName,
                        {
                            type: 'image/jpeg',
                        }
                    )
                    await uploadFile(croppedFile)
                    return true
                } catch (error) {
                    const errorMessage =
                        error instanceof Error
                            ? error.message
                            : 'Auto-crop failed'
                    setUploadError(errorMessage)
                    setState(UploadState.ERROR)
                    options.onError?.(errorMessage)
                    return false
                }
            }

            // Otherwise, show manual crop UI
            const localUrl = URL.createObjectURL(fileToProcess)
            objectUrlsRef.current.add(localUrl)
            setSelectedImageUrl(localUrl)
            setState(UploadState.CROPPING)
            return true
        },
        [
            options,
            cropConfig.required,
            cropConfig.autoCrop,
            cropConfig.aspectRatio,
            config.defaultFileName,
            uploadFile,
        ]
    )

    // File selection handler
    const handleFileChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0]
            if (!file) return
            processFile(file)
        },
        [processFile]
    )

    // Direct file handler for drag-and-drop
    const handleDirectFile = useCallback(
        (file: File) => {
            return processFile(file)
        },
        [processFile]
    )

    // Crop completion handler
    const handleCropComplete = useCallback(
        async (croppedImageBlob: Blob) => {
            // Clean up the cropper image URL
            cleanupObjectUrl(selectedImageUrl)
            setSelectedImageUrl('')

            // Convert blob to file with proper name
            const file = new File([croppedImageBlob], config.defaultFileName, {
                type: 'image/jpeg',
            })

            // Use shared upload logic
            await uploadFile(file)
        },
        [selectedImageUrl, cleanupObjectUrl, config.defaultFileName, uploadFile]
    )

    // Remove handler
    const handleRemove = useCallback(async () => {
        setPreviewUrl('')
        setState(UploadState.IDLE)

        // Auto-save removal if configured
        if (options.autoSave) {
            try {
                await options.autoSave.mutation.mutateAsync({
                    id: options.autoSave.entityId,
                    data: { [options.autoSave.field]: undefined },
                })
                options.autoSave.onSuccess?.('')
            } catch (error) {
                console.error('Auto-save removal failed:', error)
                // Revert on error
                setPreviewUrl(options.initialPreview || '')
            }
        }
    }, [options])

    // Cropper close handler
    const handleCropperClose = useCallback(() => {
        cleanupObjectUrl(selectedImageUrl)
        setSelectedImageUrl('')
        setState(UploadState.IDLE)
    }, [selectedImageUrl, cleanupObjectUrl])

    // Main actions
    const triggerUpload = useCallback(() => {
        setState(UploadState.FILE_PICKER_OPEN)
    }, [])

    const clearPreview = useCallback(() => {
        setPreviewUrl('')
    }, [])

    const clearError = useCallback(() => {
        setUploadError(null)
        setState(UploadState.IDLE)
    }, [])

    // Ready-to-use bottom sheet props
    const filePickerProps: FilePickerProps = {
        open: state === UploadState.FILE_PICKER_OPEN,
        onClose: () => setState(UploadState.IDLE),
        onFileChange: handleFileChange,
        onRemove: previewUrl ? handleRemove : undefined,
        showRemoveButton: !!previewUrl,
    }

    const cropperProps: CropperProps = {
        open: state === UploadState.CROPPING,
        onClose: handleCropperClose,
        imageUrl: selectedImageUrl,
        onCropComplete: handleCropComplete,
        aspectRatio: cropConfig.aspectRatio,
        cropShape: cropConfig.shape,
        title: cropConfig.title,
    }

    return {
        // Current state
        previewUrl,
        isUploading: [
            UploadState.PROCESSING,
            UploadState.UPLOADING,
            UploadState.AUTO_SAVING,
        ].includes(state),
        uploadError,
        currentState: state,

        // Actions
        triggerUpload,
        clearPreview,
        clearError,
        handleDirectFile,

        // Ready-to-use props
        filePickerProps,
        cropperProps,
    }
}
